import { ReactNode, Suspense, useMemo } from 'react';
import { Outlet, Route, Routes, Link, useParams, useSearchParams, useLocation } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { shallow } from 'zustand/shallow';
import { Box, Grid } from '@mui/material';
import { Button } from '@components/button';
import { HeaderType } from '@components/header/Header.constants';
import { HeaderWrapper } from '@components/headerWrapper/HeaderWrapper';
import { FullscreenLoading } from '@components/Loading';
import { Background } from '@layout/Layout.constants';
import { QuoteStatus, TQuoteApiResponse } from '@models/quotes';
import { useQuote } from '@services/quotes';
import { useStore } from '@store/GlobalStore';
import { useLayoutStore } from '@store/LayoutStore';
import { AlertList } from '@components/alert';
import { GetAppointedHeader } from '@pages/GetAppointed/components/GetAppointedHeader';

export const RootLayout = () => {
  const [isUsingPaymentPortal] = useStore((state) => [state.isUsingPaymentPortal], shallow);
  const location = useLocation();

  // Skip Suspense for portal-admin routes to prevent navigation flashing
  const isPortalAdminRoute = location.pathname.startsWith('/portal-admin');

  return (
    <Box>
      <Grid container direction='column' flexWrap='nowrap' sx={{ height: '100vh' }}>
        <Routes>
          <Route path='login' element={<AlertList />} />
          <Route path='sign-up' element={null} />
          <Route path='get-appointed/*' element={<GetAppointedHeader />} />
          <Route path='get-appointed-preview/*' element={<GetAppointedHeader />} />
          <Route path='new-quote/*' element={<NewQuotePageHeader />} />
          <Route path='payments/*' element={<HeaderWrapper darkBackground type={HeaderType.Payments} />} />
          <Route path='payments/new/*' element={<HeaderWrapper type={HeaderType.MakeAPayment} smallLogo={true} />} />
          <Route
            path='payments/statement/*'
            element={<HeaderWrapper type={HeaderType.MakeAPayment} smallLogo={true} />}
          />
          <Route path='portal-admin/*' element={<HeaderWrapper type={HeaderType.Admin} />} />
          <Route path='quote/:id' element={<QuoteDetailPageHeader />} />
          <Route path='quote/:id/bind-in-progress' element={null} />
          <Route path='quote/:id/review' element={<HeaderPlaceholder />} />
          <Route
            path='settings/*'
            element={
              isUsingPaymentPortal ? (
                <HeaderWrapper smallLogo type={HeaderType.Payments} />
              ) : (
                <HeaderWrapper smallLogo type={HeaderType.Settings} />
              )
            }
          />
          <Route path='/' element={<HeaderWrapper darkBackground title='Quotes' type={HeaderType.NavTransparent} />} />
          <Route path='*' element={<HeaderWrapper darkBackground type={HeaderType.Nav} />} />
        </Routes>

        {isPortalAdminRoute ? (
          <Outlet />
        ) : (
          <Suspense fallback={<FullscreenLoading />}>
            <Outlet />
          </Suspense>
        )}
      </Grid>
    </Box>
  );
};

// TODO: move these to their own files if we don't hate this refactor
const HeaderPlaceholder = () => {
  const customHeaderRenderedInLayout = useLayoutStore((state) => state.customHeaderRenderedInLayout);
  return customHeaderRenderedInLayout ? null : <HeaderWrapper title='Quotes' type={HeaderType.PlaceHolder} />;
};

const NewQuotePageHeader = () => {
  const { isCustomerUser, isInternalUser } = useStore((state) => state.userAuth, shallow);

  const [searchParams] = useSearchParams();
  const asRenewalParam = searchParams.get('asRenewal');

  const isRenewalMode = useMemo(() => asRenewalParam === 'true' && !!isInternalUser, [asRenewalParam, isInternalUser]);

  return (
    <>
      <Background />
      <HeaderWrapper
        navTitle={isRenewalMode ? 'New Renewal Setup' : 'New Quote Setup'}
        type={HeaderType.NewQuote}
        darkBackground
        buttons={[
          {
            type: 'link',
            linkProps: {
              to: '/',
            },
            buttonProps: {
              children: 'Cancel',
              variant: 'outlined',
              sx: { visibility: isCustomerUser ? 'hidden' : 'visible' }, // title is very off center without a button, so just hide it for now
            },
          },
        ]}
      />
    </>
  );
};

const QuoteDetailPageHeader = () => {
  const { isInternalUser, isCustomerUser } = useStore((state) => state.userAuth, shallow);
  const customHeaderRenderedInLayout = useLayoutStore((state) => state.customHeaderRenderedInLayout);

  const { quoteId } = useParams<'quoteId'>();
  invariant(quoteId);
  const quoteData = useQuote(quoteId);
  const quote = quoteData.quote as TQuoteApiResponse;

  const [searchParams] = useSearchParams();
  const isReadOnly = searchParams.has('viewReadOnly');

  let showBlockingPage = false;
  if (!isInternalUser && !isReadOnly) {
    switch (quote?.status.id) {
      case QuoteStatus.Bound.id:
      case QuoteStatus.Canceled.id:
      case QuoteStatus.Declined.id:
      case QuoteStatus.Expired.id:
      case QuoteStatus.Rejected.id:
      case QuoteStatus.Referred.id:
        showBlockingPage = true;
        break;
      default:
        break;
    }
  }

  if (!quote?.status.id || customHeaderRenderedInLayout) {
    return null;
  }

  if (showBlockingPage) {
    const headerButton: ReactNode[] = !isCustomerUser
      ? [
          <Link to='/' key='root-link'>
            <Button variant='outlined'>Exit to Homepage</Button>
          </Link>,
        ]
      : [];

    return <HeaderWrapper title='Quotes' darkBackground type={HeaderType.NewQuote} buttons={headerButton} />;
  }

  return <HeaderWrapper title='Quotes' type={HeaderType.PlaceHolder} />;
};
